import React, { useState, useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const item = payload[0].payload;
    return (
      <div className="bg-gray-900/90 text-white text-xs px-2.5 py-1.5 rounded-lg shadow-xl backdrop-blur-sm pointer-events-none flex items-center gap-2 whitespace-nowrap z-50">
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
        <span className="font-medium text-gray-200">{item.label}:</span>
        <span className="font-bold text-white">
          {item.count} <span className="text-gray-300 font-normal">({item.percent}%)</span>
        </span>
      </div>
    );
  }
  return null;
};

export default function DeadlineDonutCard({ data = [], isLoading = false, timeLabel = '' }) {
  const [activeIndex, setActiveIndex] = useState(null);

  const formattedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const rawData = data.map((item) => ({
      ...item,
      count: Number(item.count) || 0,
    }));
    const total = rawData.reduce((sum, item) => sum + item.count, 0);

    return rawData.map((item) => ({
      ...item,
      percent: total > 0 ? Number(((item.count / total) * 100).toFixed(1)) : 0,
    }));
  }, [data]);

  const totalCount = useMemo(() => {
    return formattedData.reduce((sum, item) => sum + item.count, 0);
  }, [formattedData]);

  return (
    <div className="bg-white rounded-lg p-4 md:p-5 shadow-xs border border-gray-200 h-full min-w-0 flex flex-col justify-between">
      {/* Header */}
      <h3 className="text-xs md:text-sm font-bold text-gray-800 uppercase tracking-wider mb-2">
        Đúng hạn và trễ hạn {timeLabel ? `(${timeLabel})` : ''}
      </h3>

      {isLoading ? (
        <div className="flex items-center justify-between gap-3 flex-1 animate-pulse">
          <div className="w-36 h-36 rounded-full border-4 border-gray-100 border-t-emerald-400 animate-spin flex-shrink-0" />
          <div className="space-y-2 flex-1">
            {Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className="h-4 bg-gray-100 rounded w-full" />
            ))}
          </div>
        </div>
      ) : formattedData.length === 0 ? (
        <div className="flex items-center justify-center flex-1 py-12 text-xs md:text-sm text-gray-400 font-medium">
          Chưa có dữ liệu hạn xử lý
        </div>
      ) : (
        /* Content layout: Donut left + Legend right */
        <div className="flex items-center justify-between gap-3 flex-1">
          {/* Donut Chart */}
          <div className="relative w-36 h-36 flex-shrink-0 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={144}>
              <PieChart>
                <Pie
                  data={formattedData}
                  cx="50%"
                  cy="50%"
                  innerRadius={42}
                  outerRadius={62}
                  paddingAngle={3}
                  dataKey="count"
                  stroke="#ffffff"
                  strokeWidth={2}
                  onMouseEnter={(_, index) => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                >
                  {formattedData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                      opacity={activeIndex === null || activeIndex === index ? 1 : 0.55}
                      className="transition-opacity duration-200 cursor-pointer"
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} wrapperStyle={{ outline: 'none', zIndex: 100 }} />
              </PieChart>
            </ResponsiveContainer>

            {/* Center Label */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-xl font-extrabold text-gray-900 leading-none">{totalCount}</span>
              <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mt-0.5">TỔNG</span>
            </div>
          </div>

          {/* Legend List */}
          <div className="space-y-2 flex-1 min-w-0">
            {formattedData.map((item, idx) => {
              const isSelected = activeIndex === idx;
              return (
                <div
                  key={idx}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onMouseLeave={() => setActiveIndex(null)}
                  className={`flex items-center justify-between py-1 px-1.5 rounded-lg transition-colors cursor-pointer ${
                    isSelected ? 'bg-gray-100/80' : 'hover:bg-gray-50/80'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: item.color }} />
                    <span className="text-xs font-medium text-gray-700 truncate">{item.label}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs flex-shrink-0">
                    <span className="font-bold text-gray-900">{item.count}</span>
                    <span className="text-gray-400 font-normal">({item.percent}%)</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
